import React, { FC, useEffect, useState, useRef } from 'react';
import Tour from 'reactour';
import { Button, Typography } from 'antd';
import { post } from 'utils/fetch';
import { getLangKey } from 'models/Language';
import useMediaQuery from '../../../../../clients/sidebar/views/CreateTask/create_task/useMediaQuery';
import styles from './index.module.css';
import * as translations from './strings';

const strings = translations[getLangKey()];

const taskElement =
  '[data-intercom-target="Task"]:not([data-rbd-drag-handle-draggable-id="null"])';
const taskDetailsElement = '#task_details_container';
const shareButtonElement = '.shareButtonElement';
const setupGuideElement = '.setupGuideNavItem';

type Props = {
  projectId: number | null;
  closeTask: () => void;
  setActiveTask: (activeTask: number) => void;
  currentUserId: number;
};

type Step = {
  selector: string;
  content: React.JSX.Element;
  action?: (node: Node) => void;
  active: boolean;
  showButtons?: boolean;
};

const ProjectTour: FC<Props> = ({
  projectId,
  closeTask,
  setActiveTask,
  currentUserId
}) => {
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [activeSteps, setActiveSteps] = useState<Step[]>([]);
  const isMobileView = useMediaQuery('(max-width: 640px)');
  const tourStepRef = useRef(tourStep);
  const activeStepsRef = useRef(activeSteps);

  useEffect(() => {
    tourStepRef.current = tourStep;
  }, [tourStep]);

  useEffect(() => {
    activeStepsRef.current = activeSteps;
  }, [activeSteps]);

  const goToNextTourStep = () => {
    // if last step, close the tour
    if (tourStepRef.current === activeStepsRef.current.length - 1) {
      setIsTourOpen(false);
      return;
    }
    setTourStep(prevTourStep => prevTourStep + 1);
  };
  const goToPrevTourStep = () => setTourStep(prevTourStep => prevTourStep - 1);

  const isAssetsPage = window.location.pathname.includes('assets');
  const isSetupGuidePage = window.location.pathname.includes('getting-started');
  const isKanbanPage =
    window.location.pathname.includes('kanban') ||
    window.location.pathname.includes('tasks');

  const removePulseIndicator = (): void => {
    const currentPulseIndicator = document.querySelector('#pulse-indicator');
    if (currentPulseIndicator) {
      currentPulseIndicator.remove();
    }
  };

  const addPulseToNode = (node: HTMLElement, className: string): void => {
    removePulseIndicator();
    const newDiv = document.createElement('div');
    newDiv.className = className;
    newDiv.id = 'pulse-indicator';
    node.appendChild(newDiv);
  };

  const handleFinishTour = async () => {
    removePulseIndicator();
    localStorage.setItem(
      `${currentUserId}_finished_project_onboarding`,
      'true'
    );
    try {
      await post(
        `/projects/${projectId}/complete_project_board_onboarding`,
        {}
      );
    } catch (error) {
      console.error({ error });
    }
  };

  const handleClickStep = node => {
    const handleOnClick = () => {
      goToNextTourStep();
      node.removeEventListener('click', handleOnClick);
    };
    node.addEventListener('click', handleOnClick);
  };

  const handleClickTask = node => {
    const handleShowTaskDetailsTour = e => {
      e.preventDefault();
      e.stopPropagation();
      setActiveTask(node.getAttribute('data-task-id'));
      goToNextTourStep();
      node.removeEventListener('click', handleShowTaskDetailsTour);
      node.removeEventListener('mousedown', handleShowTaskDetailsTour);
    };
    node.addEventListener('click', handleShowTaskDetailsTour);
    node.addEventListener('mousedown', handleShowTaskDetailsTour);
  };

  const getTourSteps = () => [
    {
      selector: taskElement,
      content: (
        <>
          <Typography.Title level={3} className={styles.tooltipHeader}>
            {strings.taskElementTitle}
          </Typography.Title>
          <p>{strings.taskElementDescription}</p>
          <p>
            <strong>{strings.taskElementAction}</strong>
          </p>
        </>
      ),
      action: node => {
        addPulseToNode(node, styles.selectorPulseIndicator);
        handleClickTask(node);
      },
      active: !!document.querySelector(taskElement) && isKanbanPage
    },
    {
      selector: taskDetailsElement,
      content: (
        <>
          <Typography.Title level={3} className={styles.tooltipHeader}>
            {strings.taskDetailsTitle}
          </Typography.Title>
          <p>{strings.taskDetailsDescription} </p>
          <div className={styles.nextButtonContainer}>
            <Button
              type="primary"
              onClick={() =>
                window.open(`/projects/${projectId}/assets`, '_self')
              }
            >
              {strings.next}
              <div className={styles.pulseIndicator} />
            </Button>
          </div>
        </>
      ),
      action: removePulseIndicator,
      active: !!document.querySelector(taskDetailsElement) && isKanbanPage,
      showButtons: false
    },
    {
      selector: shareButtonElement,
      content: (
        <>
          <Typography.Title level={3} className={styles.tooltipHeader}>
            {strings.shareButtonTitle}
          </Typography.Title>
          <p>{strings.shareButtonAction1}</p>
          <p>{strings.shareButtonAction2}</p>
          <div className={styles.nextButtonContainer}>
            <Button
              type="primary"
              onClick={() =>
                window.open(`/projects/${projectId}/getting-started`, '_self')
              }
            >
              {strings.next}
              <div className={styles.pulseIndicator} />
            </Button>
          </div>
        </>
      ),
      action: removePulseIndicator,
      active: !!document.querySelector(shareButtonElement) && isAssetsPage,
      showButtons: false
    },
    {
      selector: setupGuideElement,
      content: (
        <>
          <Typography.Title level={3} className={styles.tooltipHeader}>
            {strings.setupGuideTitle}
          </Typography.Title>
          <p>{strings.setupGuideDescription}</p>
        </>
      ),
      action: node => {
        addPulseToNode(node, styles.pulseIndicator);
        handleClickStep(node);
      },
      active: !!document.querySelector(setupGuideElement) && isSetupGuidePage,
      showButtons: true
    }
  ];

  const initiateTour = () => {
    const initialActiveSteps = getTourSteps().filter(step => step.active);
    if (initialActiveSteps.length === 0) return;
    setActiveSteps(initialActiveSteps);
    setIsTourOpen(true);
  };

  const checkForElement = () => {
    const delay = isAssetsPage || isSetupGuidePage;
    let element = taskElement;
    if (isAssetsPage) {
      element = shareButtonElement;
    } else if (isSetupGuidePage) {
      element = setupGuideElement;
    }
    const MAX_TRIES = 50;
    let tries = 0;

    const intervalId = setInterval(() => {
      if (tries >= MAX_TRIES) {
        clearInterval(intervalId);
        return;
      }

      const targetElement = document.querySelector(element);
      if (targetElement) {
        clearInterval(intervalId);
        if (delay) {
          setTimeout(initiateTour, 500);
        } else {
          initiateTour();
        }
      }

      tries++;
    }, 200);

    return () => clearInterval(intervalId);
  };

  useEffect(() => {
    if (isMobileView) return;
    if (isKanbanPage || isAssetsPage || isSetupGuidePage) checkForElement();
  }, []);

  useEffect(() => {
    if (!isTourOpen) return;

    const initialActiveSteps = getTourSteps().filter(step => step.active);
    setActiveSteps(initialActiveSteps);
  }, [tourStep]);

  return (
    <Tour
      className="antd-container"
      steps={activeSteps}
      isOpen={isTourOpen}
      onRequestClose={() => setIsTourOpen(false)}
      accentColor="#3f71e0"
      closeWithMask={false}
      disableDotsNavigation
      goToStep={tourStep}
      rounded={12}
      scrollDuration={500}
      showNavigation={false}
      showCloseButton={false}
      showButtons={activeSteps[tourStep]?.showButtons || false}
      prevStep={goToPrevTourStep}
      onBeforeClose={handleFinishTour}
      nextButton={
        <Button type="primary">
          {strings.next}
          <div className={styles.pulseIndicator} />
        </Button>
      }
      lastStepNextButton={
        <Button type="primary">
          {strings.finishTour}
          <div className={styles.pulseIndicator} />
        </Button>
      }
      prevButton={<></>}
      nextStep={() => {
        if (activeSteps[tourStep]?.selector === taskDetailsElement) {
          closeTask();
          goToNextTourStep();
        } else {
          goToNextTourStep();
        }
      }}
    />
  );
};

export default ProjectTour;
